
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Loader2, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Results } from "@/components/assessment/Results";
import { AssessmentRecord, OrganizationDetails, Answer } from "@/types/assessment";
import { PageHeader } from "@/components/PageHeader";
import { Footer } from "@/components/Footer";

const AssessmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: assessment, isLoading } = useQuery({
    queryKey: ["assessment-detail", id],
    queryFn: async () => {
      // Fetch from assessments table
      const { data, error } = await supabase
        .from("assessments")
        .select("*")
        .eq('id', id)
        .single();

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load assessment details",
          variant: "destructive",
        });
        throw error;
      }

      if (!data) {
        throw new Error("Assessment not found");
      }

      // Properly type-cast JSON data from Supabase to AssessmentRecord types
      const assessmentRecord: AssessmentRecord = {
        id: data.id,
        organization_details: data.organization_details as OrganizationDetails,
        answers: data.answers as Answer[],
        maturity_level: data.maturity_level,
        overall_score: data.overall_score,
        dimension_scores: data.dimension_scores as Record<string, number>,
        description: data.description,
        created_at: data.created_at,
        created_by: data.created_by || data.email || '',
      };

      return assessmentRecord;
    },
  });

  const breadcrumbs = [
    { title: "Home", href: "/" },
    { title: "Assessment History", href: "/assessment-history" },
    { title: "Assessment Details", href: `/assessment-history/${id}` },
  ];

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Assessment Not Found</h2>
          <Button onClick={() => navigate("/assessment-history")}>
            Back to Assessment History
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Assessment Details"
        description={`Assessment results for ${assessment?.organization_details.name}`}
        breadcrumbs={breadcrumbs}
      />
      <div className="container mx-auto py-8">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate("/assessment-history")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Assessment History
        </Button>

        <Results
          organizationDetails={assessment?.organization_details}
          maturityLevel={assessment?.maturity_level}
          overallScore={assessment?.overall_score}
          description={assessment?.description}
          dimensionScores={assessment?.dimension_scores}
          answers={assessment?.answers}
          onStartNew={() => navigate("/")}
          assessmentId={assessment?.id}
          isViewMode={true}
        />
      </div>
      <Footer />
    </div>
  );
};

export default AssessmentDetail;
